/* v8 ignore file — pure SVG icon exports, no logic to test */
/**
 * § 333 — the APP set, navigation. Moved here unchanged from `assests/GmsIcons.tsx`: same
 * drawings, same default sizes. What changed is the contract — every icon now takes
 * `IconProps` (`size`, defaulting to its drawn width with the height kept in proportion,
 * plus `aria-hidden`, `className`, `data-testid`…), exactly like the core set.
 *
 * These are NOT drawn to the core set's 16 grid / 1.5 stroke (that is `../Icons.tsx`,
 * pinned by `iconContract.test`). Redrawing them is a design task, not a code move.
 */
import React from 'react';
import type { IconProps } from '../Icons';
import { withClassName } from '../../cn';
import { decorative } from '../iconA11y';


/** @deprecated The core set has `ArrowRight` (Iconography/Icons), drawn to the DS grid. This app-set drawing is kept so nothing changes on screen; migrate when the design is reviewed. */
export const ArrowRightIcon = ({ size = 24, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 16) / 24 : undefined} viewBox="0 0 24 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M1 8H18M18 8L12 2M18 8L12 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const LeftArrow = ({ size = 14, ...rest }: IconProps) => (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M10.2082 7L3.7915 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.00032 10.2077L3.79199 6.99935L7.00033 3.79102" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>

);

export const RightArrow = ({ size = 14, ...rest }: IconProps) => (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M3.79183 7L10.2085 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7.00016 3.79232L10.2085 7.00065L7.00016 10.209" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>

);

export const BlockRightArrowIcon = ({ size = 25, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 19) / 25 : undefined} viewBox="0 0 25 19" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path id="Arrow 1" d="M2 8.25C1.30964 8.25 0.75 8.80964 0.75 9.5C0.75 10.1904 1.30964 10.75 2 10.75V8.25ZM23.8839 10.3839C24.372 9.89573 24.372 9.10427 23.8839 8.61612L15.9289 0.661165C15.4408 0.17301 14.6493 0.17301 14.1612 0.661165C13.673 1.14932 13.673 1.94078 14.1612 2.42893L21.2322 9.5L14.1612 16.5711C13.673 17.0592 13.673 17.8507 14.1612 18.3388C14.6493 18.827 15.4408 18.827 15.9289 18.3388L23.8839 10.3839ZM2 10.75L23 10.75V8.25L2 8.25V10.75Z" fill="currentColor" />
    </svg>
)

export const UpArrowIcon = ({ color = 'currentColor', size = 8, ...rest }: { color?: string } & IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 6) / 8 : undefined} viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path id="Vector" d="M0.837891 5.25L3.83789 0.75L6.83789 5.25L0.837891 5.25Z" fill={color} stroke={color} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const DownArrowIcon = ({ color = 'currentColor', size = 8, ...rest }: { color?: string } & IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 6) / 8 : undefined} viewBox="0 0 8 6" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path id="Vector" d="M6.83594 0.75L3.83594 5.25L0.835938 0.75L6.83594 0.75Z" fill={color} stroke={color} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const BigDownArrowIcon = ({ size = 20, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 25) / 20 : undefined} viewBox="0 0 20 25" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path id="Arrow 1" d="M11.25 2C11.25 1.30964 10.6904 0.75 10 0.75C9.30964 0.75 8.75 1.30964 8.75 2L11.25 2ZM9.11612 23.8839C9.60427 24.372 10.3957 24.372 10.8839 23.8839L18.8388 15.9289C19.327 15.4408 19.327 14.6493 18.8388 14.1612C18.3507 13.673 17.5592 13.673 17.0711 14.1612L10 21.2322L2.92893 14.1612C2.44078 13.673 1.64932 13.673 1.16117 14.1612C0.67301 14.6493 0.67301 15.4408 1.16117 15.9289L9.11612 23.8839ZM8.75 2L8.75 23L11.25 23L11.25 2L8.75 2Z" fill="currentColor" />
    </svg>
)

export const RightArrowIcon = ({ size = 9, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 8) / 9 : undefined} viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M5.24268 0.530273L8.42466 3.71225L5.24268 6.89423" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.42471 3.71231H1.00009" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const NavRightIcon = ({ size = 6, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 10) / 6 : undefined} viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M1 9L5 5L1 1" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const BackArrowIcon = ({ size = 8, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 14) / 8 : undefined} viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M7 1L0.999999 7L7 13" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const BackIcon = ({ size = 18, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 12) / 18 : undefined} viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M17 6.75C17.4142 6.75 17.75 6.41421 17.75 6C17.75 5.58579 17.4142 5.25 17 5.25L17 6.75ZM0.469669 5.46967C0.176777 5.76256 0.176777 6.23744 0.469669 6.53033L5.24264 11.3033C5.53553 11.5962 6.01041 11.5962 6.3033 11.3033C6.59619 11.0104 6.59619 10.5355 6.3033 10.2426L2.06066 6L6.3033 1.75736C6.59619 1.46447 6.59619 0.989593 6.3033 0.6967C6.01041 0.403807 5.53553 0.403807 5.24264 0.6967L0.469669 5.46967ZM17 5.25L1 5.25L1 6.75L17 6.75L17 5.25Z" fill="currentColor" />
    </svg>
)

export const MenuDownIcon = ({ size = 10, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 6) / 10 : undefined} viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const ColoredMenuDownIcon = ({ color = "currentColor", size = 10, ...rest }: { color?: string } & IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 6) / 10 : undefined} viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M1 1L5 5L9 1" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const UpArrow = ({ size = 14, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 8) / 14 : undefined} viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M13 7L7 0.999999L1 7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const SquareDownArrow = ({ size = 24, ...rest }: IconProps) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>

)

export const SquareUpArrow = ({ size = 24, ...rest }: IconProps) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <rect className="ds-glyph-sunken" x="24" y="24" width="24" height="24" rx="3" transform="rotate(-180 24 24)" fill="currentColor" />
        <path className="ds-glyph-disabled" d="M18 15L12 9L6 15" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>

)

export const PlainDownArrow = ({ size = 14, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 8) / 14 : undefined} viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M1 1L7 7L13 1" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const DownArrow = ({ size = 24, ...rest }: IconProps) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <rect className="ds-glyph-edge" width="24" height="24" rx="4" fill="currentColor" />
        <path d="M6 9L12 15L18 9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* TOMBSTONE (2026-09-08): UpArrowWhiteBG and DownArrowNoBG deleted — their only
   consumers (Category Relevancy's tables and Analysis trigger) draw the design
   system's `ChevronDown` (rotated when expanded), which is currentColor and
   themes. UpArrowWhiteBG baked a solid #FFFFFF backing rect — a glowing white
   box on a dark ground; do not recreate either here. */

export const ChevronIcon = ({ isExpanded, size = 12, className, ...rest }: { isExpanded?: boolean } & IconProps) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        className={withClassName(`tw-transition-transform tw-duration-fast ${isExpanded ? 'tw-rotate-90' : ''}`, className)} {...decorative(rest)}>
        <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
    </svg>
);

export const VectorIcon = ({ size = 8, ...rest }: IconProps) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={typeof size === 'number' ? (size * 14) / 8 : undefined} viewBox="0 0 8 14" fill="none" {...decorative(rest)}>
        <path d="M1 13L7 7L0.999999 1" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

export const LIcon = ({ size = 12, ...rest }: IconProps) => (
    <svg width={size} height={typeof size === 'number' ? (size * 24) / 12 : undefined} viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...decorative(rest)}>
        <path d="M1 0V23H12" stroke="currentColor" />
    </svg>
)
